import React, { CSSProperties } from 'react';

import propTypes from '@styled-system/prop-types';
import styledC from 'styled-components';
import {
  border,
  BorderProps,
  color,
  ColorProps,
  flexbox,
  FlexboxProps,
  grid,
  GridProps,
  layout,
  LayoutProps,
  position,
  PositionProps,
  space,
  SpaceProps,
  system,
  TypographyProps,
  typography as typographySystem,
} from 'styled-system';

import {
  flattenStyle,
  type StylableComponent,
  stylePropType,
} from '@adsk/alloy-react-theme';

import {
  addTransientPropTypes,
  preprocessStyleProp,
  shouldForwardProp,
  transient,
  type WithTransientProps,
} from './helpers';
import { useBoxTheme } from './hooks/useBoxTheme';
import {
  base,
  Colors,
  Ellipsis,
  propTypes as themePropTypes,
  Typography,
} from './Theme';
import { CSSPropertiesStyleProp } from './type';

export const boxPropTypes = addTransientPropTypes({
  ...propTypes.border,
  ...propTypes.space,
  ...propTypes.layout,
  ...propTypes.typography,
  ...propTypes.flexbox,
  ...propTypes.color,
  ...propTypes.grid,
  ...themePropTypes.color,
  ...themePropTypes.typography,
  ...themePropTypes.ellipsis,
});

boxPropTypes.style = stylePropType;

export interface IBoxBaseProps
  extends
    SpaceProps,
    PositionProps,
    LayoutProps,
    BorderProps,
    TypographyProps,
    FlexboxProps,
    GridProps,
    ColorProps,
    Omit<StylableComponent<HTMLDivElement>, 'style'>,
    Pick<
      CSSProperties,
      'gap' | 'wordBreak' | 'flexFlow' | 'cursor' | 'maskImage'
    > {
  typography?: Typography;
  color?: Colors;
  bg?: Colors;
  ellipsis?: Ellipsis;
  style?: CSSPropertiesStyleProp<HTMLAllCollection>;
}

export type IBoxProps = WithTransientProps<IBoxBaseProps>;

const StyledBox = styledC.div
  .withConfig({
    shouldForwardProp,
  })
  .attrs<IBoxProps>(preprocessStyleProp)<IBoxProps>(
  base,
  transient(space),
  transient(layout),
  transient(border),
  transient(flexbox),
  transient(color),
  transient(grid),
  transient(position),
  transient(typographySystem),
  transient(
    system({
      gap: true,
      flexFlow: true,
      cursor: true,
      maskImage: true,
      wordBreak: true,
    }),
  ),
);

const Box = React.forwardRef<HTMLElement, IBoxProps>((props, ref) => {
  const { typography, ellipsis } = useBoxTheme(props);
  const styles = flattenStyle({
    ...typography(props),
    ...ellipsis(props),
    ...(flattenStyle(props?.style) ?? {}),
  });
  return <StyledBox ref={ref} {...props} style={styles} />;
});

Box.displayName = 'Box';
Box.propTypes = boxPropTypes;

export default Box;
